import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiError, inr } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const tenures = [30, 60, 90, 180];

export default function LoanRequestModal({ open, onOpenChange, onDone }) {
  const [amount, setAmount] = useState("");
  const [tenure, setTenure] = useState(90);
  const [purpose, setPurpose] = useState("");
  const [loading, setLoading] = useState(false);

  const amt = parseFloat(amount) || 0;
  const interest = Math.round((amt * 0.12 * tenure) / 365);
  const total = amt + interest;
  const emi = Math.round(total / Math.max(1, Math.round(tenure / 30)));

  const submit = async () => {
    if (amt <= 0) return toast.error("सही राशि दर्ज करें");
    if (!purpose.trim()) return toast.error("ऋण का उद्देश्य बताएं");
    setLoading(true);
    try {
      await api.post("/loan-requests", { amount: amt, tenure_days: tenure, purpose });
      toast.success("ऋण अनुरोध भेजा गया — सदस्य मतदान करेंगे");
      setAmount(""); setPurpose(""); setTenure(90);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="loan-request-modal" className="max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">ऋण अनुरोध / Loan Request</DialogTitle>
          <DialogDescription>12% वार्षिक ब्याज • सदस्य मतदान से स्वीकृति</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>ऋण राशि / Amount (₹)</Label>
            <Input type="number" data-testid="loan-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="5000" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label>अवधि / Tenure (दिन)</Label>
            <div className="grid grid-cols-4 gap-2">
              {tenures.map((t) => (
                <button
                  key={t}
                  data-testid={`tenure-${t}`}
                  onClick={() => setTenure(t)}
                  className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    tenure === t ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>उद्देश्य / Purpose</Label>
            <Textarea data-testid="loan-purpose-input" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Medical Emergency" />
          </div>
          {amt > 0 && (
            <div className="rounded-xl bg-blue-50 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">मासिक EMI</span><span className="font-mono font-semibold">{inr(emi)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">ब्याज / Interest</span><span className="font-mono">{inr(interest)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">कुल भुगतान</span><span className="font-mono font-semibold">{inr(total)}</span></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>रद्द करें</Button>
          <Button data-testid="loan-request-submit-btn" onClick={submit} disabled={loading}>
            {loading ? "भेज रहे हैं…" : "अनुरोध भेजें"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
